import React from "react";
import { Box, Button } from "@mui/material";
import { GALLERY_CHROME, MIN_GALLERY_HEIGHT } from "./galleryLayout";

interface PaginatedGalleryProps {
  width: number; // available width, measured by the page (AutoSizer's wrapper is 0-sized)
  cols: number; // columns to lay out — chosen by the page to fill the viewport
  rows: number; // rows to lay out — likewise
  page: number;
  onPage: (page: number) => void;
  hasNext: boolean;
  height?: number; // the gallery's height; when omitted it fills the viewport (100vh - chrome)
  header?: React.ReactNode; // optional controls above the grid (e.g. a filter toggle)
  children: React.ReactNode; // exactly cols×rows cells (fewer on the last page)
}

// A gallery that fits the viewport: an optional header, an exact cols×rows grid that fills the space
// between it and the pager, and Previous/Next pinned to the bottom — no page scroll. The page sizes
// cols/rows to the viewport, so a full page never leaves a half-empty row and a resize refills it.
const PaginatedGallery: React.FC<PaginatedGalleryProps> = ({ width, cols, rows, page, onPage, hasNext, height, header, children }) => (
  <Box
    sx={{
      width: width || "100%",
      height: height ?? `calc(100vh - ${GALLERY_CHROME}px)`,
      display: "flex",
      flexDirection: "column",
      // Nothing to scroll while the viewport can hold the whole gallery, which is the usual case;
      // on one that cannot, this is what lets the grid and the pager be reached at all.
      overflowX: "hidden",
      overflowY: "auto",
    }}
  >
    {header && <Box sx={{ flexShrink: 0, mb: 1 }}>{header}</Box>}
    <Box
      sx={{
        flex: 1,
        minHeight: MIN_GALLERY_HEIGHT,
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        columnGap: 1,
      }}
    >
      {children}
    </Box>
    <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center", gap: 1, py: 1 }}>
      <Button variant="outlined" onClick={() => onPage(page - 1)} disabled={page === 1}>
        Previous
      </Button>
      <Button variant="outlined" onClick={() => onPage(page + 1)} disabled={!hasNext}>
        Next
      </Button>
    </Box>
  </Box>
);

export default PaginatedGallery;
